import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Box,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import useSWR from "swr";
import { useForm, Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import BaseLayout from "../../layouts/BaseLayout";
import StateSelect from "../../components/Reusable/StateSelect";
import DateOfSurgeryField from "./PagePatientNew/DateOfSurgeryField";
import ItemPickerField from "./PagePatientNew/ItemPickerField";
import FileUploadField from "./PagePatientNew/FileUploadField";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { formatDateFromDatePicker } from "../../util";
import { LoadingButton } from "@mui/lab";

function PagePatientNew() {
  const { api } = useOutletContext();
  const { npi } = useParams();
  const [submitError, setSubmitError] = useState(null);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const navigate = useNavigate();

  const { isLoading, error, data } = useSWR(`/prescribers/${npi}`, () =>
    api.getPrescriber(npi),
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name_first: "",
      name_last: "",
      date_of_birth: null,
      phone: "",
      email: "",
      addr_street1: "",
      addr_street2: "",
      addr_city: "",
      addr_state: "",
      addr_zip: "",
      surgical: false,
      date_of_surgery: null,
      pref_arrive_on: null,
      authorized_days: "",
      initial_rental_price: "",
      attachment_id: null,
    },
  });

  if (error) {
    return (
      <BaseLayout>
        <Alert severity="error">{error.message}</Alert>
      </BaseLayout>
    );
  }

  if (isLoading || !data) {
    return <BaseLayout>Loading...</BaseLayout>;
  }

  const onSubmit = async (formData) => {
    setSubmitError(null);

    const authorized_days = parseInt(formData.authorized_days, 10);
    const initial_rental_price = Math.round(
      parseFloat(formData.initial_rental_price) * 100,
    );
    const date_of_birth = formatDateFromDatePicker(formData.date_of_birth);
    const date_of_surgery = formatDateFromDatePicker(formData.date_of_surgery);
    const pref_arrive_on = formatDateFromDatePicker(formData.pref_arrive_on);
    const payload = {
      ...formData,
      phone: formData.phone.replaceAll(/[^0-9]/g, ""),
      authorized_days,
      initial_rental_price,
      date_of_birth,
      date_of_surgery,
      pref_arrive_on,
      npi,
    };

    try {
      const result = await api.submitNewPatient(payload);
      console.log("SUCCESS RESULT", result);
      navigate(`/service/${result.treatId}`);
    } catch (err) {
      console.log("ERROR RESULT", err.message);
      setSubmitError(
        err.message || "Failed to submit patient. Please try again.",
      );
    }
  };

  const prescriberName = [data.firstName, data.lastName, data.credential]
    .filter(Boolean)
    .join(" ");

  return (
    <BaseLayout>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Typography variant="h3">New Patient</Typography>
          <Alert severity="info">
            This patient is being added under prescriber{" "}
            <strong>{prescriberName}</strong>. This is a{" "}
            <strong>cash pay rental</strong> order.
          </Alert>
          <Alert severity="error">
            ONLY ADD PATIENTS HERE IF YOU PLAN TO FIT THE PATIENT{" "}
            <strong>IN PERSON</strong>! If you are looking to have WRS drop-ship
            the unit ot the patient, email grintake@wrspecialists.com instead!
          </Alert>

          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid xs={12} sm={6}>
              <TextField
                {...register("name_first", {
                  required: "First name is required",
                })}
                fullWidth
                label="Patient First Name"
                error={!!errors.name_first}
                helperText={errors.name_first?.message}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                {...register("name_last", {
                  required: "Last name is required",
                })}
                fullWidth
                label="Patient Last Name"
                error={!!errors.name_last}
                helperText={errors.name_last?.message}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Controller
                name="date_of_birth"
                control={control}
                rules={{
                  required: "Date of birth is required",
                }}
                render={({ field }) => (
                  <MobileDatePicker
                    {...field}
                    label="Date of Birth"
                    inputFormat="MM/DD/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!errors.date_of_birth}
                        helperText={errors.date_of_birth?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone is required",
                  pattern: {
                    value: /^\d{3}-\d{3}-\d{4}$/,
                    message: "Phone must be in format: 555-555-5555",
                  },
                }}
                render={({ field }) => (
                  <InputMask mask="999-999-9999" {...field}>
                    {(inputProps) => (
                      <TextField
                        {...inputProps}
                        label="Phone"
                        fullWidth
                        error={!!errors.phone}
                        helperText={
                          errors.phone?.message ||
                          "Phone number used by WRS for support and reminders"
                        }
                      />
                    )}
                  </InputMask>
                )}
              />
            </Grid>
            <Grid xs={12} sm={12} md={6}>
              <TextField
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                label="Email"
                fullWidth
                error={!!errors.email}
                helperText={
                  errors.email?.message ||
                  "Email that the Rental Agreement will be sent to"
                }
              />
            </Grid>
            <Grid xs={12} sm={8}>
              <TextField
                {...register("addr_street1", {
                  required: "Street address is required",
                })}
                label="Street Address"
                fullWidth
                error={!!errors.addr_street1}
                helperText={errors.addr_street1?.message}
              />
            </Grid>
            <Grid xs={12} sm={4}>
              <TextField
                {...register("addr_street2")}
                label="Suite / Apt"
                fullWidth
              />
            </Grid>
            <Grid xs={12} sm={5}>
              <TextField
                {...register("addr_city", {
                  required: "City is required",
                })}
                label="City"
                fullWidth
                error={!!errors.addr_city}
                helperText={errors.addr_city?.message}
              />
            </Grid>
            <Grid xs={7} sm={4}>
              <Controller
                name="addr_state"
                control={control}
                rules={{ required: "State is required" }}
                render={({ field, fieldState: { error } }) => (
                  <StateSelect
                    fullWidth
                    value={field.value}
                    onChange={field.onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid xs={5} sm={3}>
              <TextField
                {...register("addr_zip", {
                  required: "Zip code is required",
                  pattern: {
                    value: /^\d{5}$/,
                    message: "Invalid zip code (e.g., 12345)",
                  },
                })}
                label="Zip"
                fullWidth
                error={!!errors.addr_zip}
                helperText={errors.addr_zip?.message}
              />
            </Grid>
            <Grid xs={12} md={4} sx={{ alignItems: "center", display: "flex" }}>
              <Controller
                name="surgical"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Surgical"
                  />
                )}
              />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <DateOfSurgeryField control={control} setValue={setValue} />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <Controller
                name="pref_arrive_on"
                control={control}
                rules={{
                  required: "Target fitting date is required",
                }}
                render={({ field }) => (
                  <MobileDatePicker
                    {...field}
                    label="Target Fitting Date"
                    inputFormat="MM/DD/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!errors.date_of_surgery}
                        helperText={
                          errors.date_of_surgery?.message ??
                          "If you are unsure, pick one day before surgery."
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid xs={12}>
              <Divider sx={{ my: 2 }}>Order</Divider>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <TextField
                {...register("authorized_days", {
                  required: "Initial rental days is required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Must be a valid number",
                  },
                  min: {
                    value: 1,
                    message: "Must be at least 1 day",
                  },
                })}
                label="Initial Rental Days"
                type="number"
                fullWidth
                error={!!errors.authorized_days}
                helperText={
                  errors.authorized_days?.message ||
                  "Amount of days for the initial rental"
                }
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <TextField
                {...register("initial_rental_price", {
                  required: "Initial rental price is required",
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Must be a valid price (e.g., 99.99)",
                  },
                })}
                InputProps={{
                  startAdornment: "$",
                }}
                label="Initial Rental Price"
                fullWidth
                error={!!errors.initial_rental_price}
                helperText={errors.initial_rental_price?.message}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <Controller
                name="consumable_id"
                control={control}
                rules={{ required: "Wrap is required" }}
                render={({ field, fieldState: { error } }) => (
                  <ItemPickerField
                    value={field.value}
                    onChange={field.onChange}
                    error={!!error}
                    helperText={error?.message}
                    label="Select a Wrap"
                    therapy="gr"
                    itemType="consumable"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid xs={12}>
              <Divider sx={{ my: 2 }}>Prescription</Divider>
            </Grid>
            <Grid xs={12}>
              {data.sigs.includes("gr") && !showDocUpload ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setShowDocUpload(true)}
                    >
                      Upload Hard Copy Anyway
                    </Button>
                  }
                >
                  Supporting documentation is not required for this prescriber.
                </Alert>
              ) : (
                <Controller
                  name="attachment_id"
                  control={control}
                  rules={
                    data.sigs.includes("gr")
                      ? {}
                      : { required: "Supporting document is required" }
                  }
                  render={({ field, fieldState: { error } }) => (
                    <FileUploadField
                      value={field.value}
                      onChange={field.onChange}
                      error={!!error}
                      helperText={
                        error?.message ||
                        "Upload prescription or referral document"
                      }
                      label="Upload Supporting Document"
                      accept="image/*,application/pdf"
                      maxSizeMB={25}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>

          <LoadingButton
            type="submit"
            size="large"
            variant="contained"
            sx={{ p: 2 }}
            loadingPosition="start"
            loading={isSubmitting}
          >
            Add Patient
          </LoadingButton>
        </Stack>
      </Box>
    </BaseLayout>
  );
}

export default PagePatientNew;
